<script setup lang="ts">
import { computed, ref } from 'vue'
import { useBrxmData } from '../../composables/useBrxmData'
import { useImage } from '../../composables/useImageUrl'
import { useCTAButton, useCTALink } from '../CTABlock/ts/useCTAButtons'
import { ECTABlockButtonAlignment } from '../CTABlock/ts/types'
import { EButtonsOrientation, EImageOrVideoAlignment } from './ts/types'
import type { IBrxmProps } from '@/cms-ui/ts/interfaces/IBrxmProps'
import { ETitleBlockSizes } from '@/cms-ui/components/TitleBlock/ts/types'
import { useVisibilityTracking } from '@/cms-ui/composables/useAnalytics'

const props = defineProps<IBrxmProps>()

// Brxm
const data = useBrxmData(props, 'RICH_CONTENT_BLOCK')

const {
    darkmode,
    badges,
    title,
    seoHeadingSize,
    subtitle,
    size,
    bodyTexts,
    button1,
    button2,
    link,
    imageOrVideoAlignment,
    buttonsAlignment,
    buttonsOrientation,
    legal,
    imageOrVideo,
    background,
    showImageForDesktopOnly,
} = data.value

const button1Config = useCTAButton(button1)
const button2Config = useCTAButton(button2)
const linkConfig = useCTALink(link)
const { image: responsiveImage } = useImage(imageOrVideo, props.page)

const richContentBlockRef = ref<HTMLElement | null>(null)

useVisibilityTracking(richContentBlockRef, 'RichContentBlock', title || 'RichContentBlock')

const computedClass = computed(() => {
    if (imageOrVideo && imageOrVideoAlignment === EImageOrVideoAlignment.LEFT) {
        return 'lg:col-start-8 order-2'
    } else if (imageOrVideo) {
        return 'lg:col-start-2'
    } else {
        return 'lg:col-span-12'
    }
})

const paddingClass = computed(() => {
    if (data?.value?.size === ETitleBlockSizes.H1 || data?.value?.size === ETitleBlockSizes.H2) {
        return 'pt-large'
    }
    return 'pt-medium'
})
</script>

<template>
    <BackgroundBlock
        ref="richContentBlockRef"
        class="xxxl:rounded-none xxl:rounded-container-md mx-auto max-w-[2560px] bg-center bg-no-repeat"
        :class="background ? 'lg:py-3xl md:py-2xl py-xl space-background' : 'space-standard'"
        :page="page"
        :background="background"
    >
        <div class="container">
            <div class="gap-x-md grid grid-cols-4 lg:grid-cols-12">
                <div
                    class="col-span-4 flex flex-col justify-center"
                    :class="computedClass"
                >
                    <TitleBlockPartsTitleBlockComponent
                        v-if="title || subtitle || badges"
                        :heading-block-aligment="data.alignment"
                        :heading-block-size="size"
                        :heading="title"
                        :seo-heading-size="seoHeadingSize?.value"
                        :subheading="subtitle"
                        :badges="badges"
                        :dark="darkmode"
                        class="px-0"
                        :class="{ [paddingClass]: !background }"
                    />

                    <div
                        class="rich-editor gap-x-md paragraph-md text-neutral-primary mt-base grid grid-cols-4 lg:grid-cols-12"
                        :class="{ dark: darkmode }"
                    >
                        <div
                            v-html-gpp="bodyTexts[0]?.value"
                            class="col-span-4"
                            :class="bodyTexts[1]?.value ? 'lg:col-span-6' : 'lg:col-span-12'"
                        />
                        <div
                            v-if="!imageOrVideo"
                            class="col-span-4 lg:col-span-6"
                        >
                            <div
                                v-if="bodyTexts[1]"
                                v-html-gpp="bodyTexts[1]?.value"
                            />
                        </div>
                    </div>

                    <CTABlock
                        v-if="button1 || button2 || link"
                        class="mt-lg"
                        :dark="darkmode"
                        :alignment="ECTABlockButtonAlignment.LEFT"
                        :alignment-desktop="buttonsAlignment"
                        :inline="buttonsOrientation === EButtonsOrientation.INLINE"
                        :cta-button1="button1Config"
                        :cta-button2="button2Config"
                        :cta-link="linkConfig"
                        :page="page"
                        component-category="RichContentBlock"
                        :component-name="title"
                    />

                    <BaseImage
                        v-if="imageOrVideo && !showImageForDesktopOnly"
                        picture-classes="mt-lg lg:hidden"
                        class="rounded-container-lg w-full"
                        :image-data="responsiveImage"
                        :alt="imageOrVideo?.altText"
                    />

                    <div
                        v-if="legal"
                        v-html-gpp="legal.value"
                        class="mt-lg paragraph-xs text-neutral-primary"
                        :class="{ dark: darkmode }"
                    />
                </div>

                <div
                    v-if="imageOrVideo"
                    class="col-span-6 hidden items-center lg:flex"
                    :class="
                        imageOrVideoAlignment === EImageOrVideoAlignment.LEFT ? 'col-start-1 order-1' : 'col-start-7'
                    "
                >
                    <BaseImage
                        class="rounded-container-lg w-full"
                        :image-data="responsiveImage"
                        :alt="imageOrVideo?.altText"
                    />
                </div>
            </div>
        </div>
    </BackgroundBlock>
</template>

<style>
.rich-editor img {
  display: inline-block;
}

.pt-large {
  @apply pt-xl lg:pt-2xl;
}

.pt-medium {
  @apply pt-md md:pt-lg lg:pt-xl;
}
</style>
